import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { localDateTimeToApi } from './useDateFormatter';
import type { Agenda_AlterarCompromissoResponse } from '../contracts/Agenda_AlterarCompromissoResponse';
import type { Agenda_BuscarFormasPagamentoInput } from '../contracts/Agenda_BuscarFormasPagamentoInput';
import type { Compromissos_FinalizaCompromissoPorIdResponse } from '../contracts/Compromissos_FinalizaCompromissoPorIdResponse';
import type { Compromissos_ListaCompromissosInput } from '../contracts/Compromissos_ListaCompromissosInput';
import type { Compromissos_ListaCompromissosResponse } from '../contracts/Compromissos_ListaCompromissosResponse';
import '../contracts/Agenda_AgendarCompromissoInput';
import '../contracts/Agenda_AlterarCompromissoInput';
import '../contracts/Agenda_BuscarFormasPagamentoInput';
import '../contracts/Agenda_DeletarCompromissoInput';
import '../contracts/Compromissos_CriarReciboInput.ts';
import '../contracts/Compromissos_ListaCompromissosInput';
import '../contracts/Agenda_AgendarCompromissoResponse';
import '../contracts/Agenda_AlterarCompromissoResponse';
import '../contracts/Agenda_BuscarCompromissoResponse';
import '../contracts/Agenda_BuscarFormasPagamentoResponse';
import '../contracts/Agenda_DeletarCompromissoResponse';
import '../contracts/Compromissos_BuscaReciboResponse';
import '../contracts/Compromissos_CancelarCompromissoResponse';
import '../contracts/Compromissos_CriarReciboResponse';
import '../contracts/Compromissos_FinalizaCompromissoPorIdResponse';
import '../contracts/Compromissos_ListaCompromissosResponse';
import '../contracts/Compromissos_RemoverCompromissoResponse';

import '../contracts/Agenda_DeletaCompromissoServicoResponse';
import type { Agenda_AgendarCompromissoInput } from '../contracts/Agenda_AgendarCompromissoInput';
import type { Agenda_AlterarCompromissoInput } from '../contracts/Agenda_AlterarCompromissoInput';
import type { Agenda_DeletarCompromissoInput } from '../contracts/Agenda_DeletarCompromissoInput';
/**
 * Composable for Compromisso (Appointment) operations
 * Provides methods to interact with the Compromissos API
 */
export function useCompromisso() {
  const isLoading = ref(false);
  const error = ref(null);
  // NAO instanciar useDateFormatter() aqui: ele chama useI18n(), que exige um
  // componente ativo. Este composable e criado DEPOIS de um `await import()`
  // (useRecebimento), quando o Vue ja perdeu esse contexto — e o vue-i18n
  // lancava MUST_BE_CALL_SETUP_TOP, exibido como "Erro ao carregar dados: 26"
  // (chamado 2026-0009). A conversao e pura e nao precisa de idioma.

  /**
   * Fetch list of compromissos with filters
   *
   * @param {Object} filters - Filter options for compromissos
   * @param {Object} options - Additional options
   * @param {Object} options.queryOverride - Override session filter (bypasses global filter)
   * @param {Array<number|string>} options.queryOverride.franquiaIds - Franchise IDs
   * @param {string} options.queryOverride.dataInicio - Start date (Y-m-d)
   * @param {string} options.queryOverride.dataFim - End date (Y-m-d)
   * @param {Array<string>} [options.queryOverride.userIds] - Optional user subs
   * @returns {Array} Compromissos list
   */
  const fetchCompromissosList = async (filters = {}, options: any = {}) => {
    log('useCompromisso', 'fetchCompromissosList', 'Fetching compromissos list', { filters, options });
    isLoading.value = true;
    error.value = null;

    try {
      let response: Compromissos_ListaCompromissosResponse;
      // Empty-Input endpoint: payload is unused by the controller. Local typed
      // declaration satisfies composable-input-not-any while preserving the
      // legacy `filters` arg shape for backward compatibility.
      const payload: Compromissos_ListaCompromissosInput = filters as Compromissos_ListaCompromissosInput;

      // If queryOverride is provided, use httpService.postWithOverride to bypass session filter
      if (options.queryOverride) {
        log('useCompromisso', 'fetchCompromissosList', 'Using query override', options.queryOverride);
        response = await httpService.postWithOverride<Compromissos_ListaCompromissosResponse>(
          '/index.php/compromissos/lista_compromissos',
          payload,
          options.queryOverride,
          {
            inputContract: 'Compromissos_ListaCompromissosInput',
            responseContract: 'Compromissos_ListaCompromissosResponse'
          }
        );
      } else {
        response = await httpService.post<Compromissos_ListaCompromissosResponse>('/index.php/compromissos/lista_compromissos', payload, {
          inputContract: 'Compromissos_ListaCompromissosInput',
          responseContract: 'Compromissos_ListaCompromissosResponse',
        });
      }

      log('useCompromisso', 'fetchCompromissosList', 'Response', response);

      if (response.success) {
        const data = response.dados || (response as { compromissos?: unknown[] }).compromissos || [];
        log('useCompromisso', 'fetchCompromissosList', 'Returning data', data.length, 'items');
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar lista de compromissos');
      }
    } catch (err) {
      log('useCompromisso', 'fetchCompromissosList', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single compromisso by ID with full details
   */
  const fetchCompromisso = async (compromissoId: number) => {
    log('useCompromisso', 'fetchCompromisso', 'Fetching compromisso', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(
        `/index.php/agenda/buscar_compromisso/${compromissoId}`,
        { responseContract: 'Agenda_BuscarCompromissoResponse' }
      );
      log('useCompromisso', 'fetchCompromisso', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Erro ao buscar compromisso');
        }
        const dados = response.dados;
        log('useCompromisso', 'fetchCompromisso', 'Compromisso data', dados.compromisso);
        log('useCompromisso', 'fetchCompromisso', 'Orcamento data', dados.orcamento);

        // API returns orcamento as {orcamento: {...}, servicos: [...]}
        const orcamentoWrapper = dados.orcamento;
        const orcamento = orcamentoWrapper?.orcamento ?? orcamentoWrapper ?? {};

        // `cliente` is not part of the canonical Agenda_BuscarCompromissoResponse
        // contract — it is provided as part of `compromisso` (compromisso.cliente,
        // compromisso.cliente_id). Surface it as null so existing callers that
        // optionally read `.cliente` keep working without type-erased access.
        const result = {
          compromisso: dados.compromisso || {},
          orcamento: orcamento,
          cliente: null,
          ocorrencias: dados.ocorrencias || []
        };

        log('useCompromisso', 'fetchCompromisso', 'Returning result', result);
        return result;
      } else {
        throw new Error(response.error || 'Erro ao buscar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'fetchCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new compromisso
   */
  const createCompromisso = async (data: Agenda_AgendarCompromissoInput) => {
    log('useCompromisso', 'createCompromisso', 'Creating compromisso', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Agenda_AgendarCompromissoInput', data);
      const response = await httpService.post('/index.php/agenda/agendar_compromisso/', data, {
        responseContract: 'Agenda_AgendarCompromissoResponse',
      });
      log('useCompromisso', 'createCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao criar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'createCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update existing compromisso
   */
  const updateCompromisso = async (input: Agenda_AlterarCompromissoInput) => {
    log('useCompromisso', 'updateCompromisso', 'Updating compromisso', input);
    isLoading.value = true;
    error.value = null;

    try {
      // App → API boundary: browser-local datetime → ISO 8601 UTC
      const data: Agenda_AlterarCompromissoInput = {
        ...input,
        compromisso_data_hora_inicio: input.compromisso_data_hora_inicio == null ? input.compromisso_data_hora_inicio : localDateTimeToApi(input.compromisso_data_hora_inicio),
        compromisso_data_hora_fim: input.compromisso_data_hora_fim == null ? input.compromisso_data_hora_fim : localDateTimeToApi(input.compromisso_data_hora_fim),
      };
      validate('Agenda_AlterarCompromissoInput', data);
      const response = await httpService.post<Agenda_AlterarCompromissoResponse>(
        '/index.php/agenda/alterar_compromisso/',
        data,
        { responseContract: 'Agenda_AlterarCompromissoResponse' }
      );
      log('useCompromisso', 'updateCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'updateCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete compromisso (remove from agenda)
   */
  const deleteCompromisso = async (compromissoId: number) => {
    log('useCompromisso', 'deleteCompromisso', 'Deleting compromisso', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Agenda_DeletarCompromissoInput = { compromisso_id: compromissoId };
      validate('Agenda_DeletarCompromissoInput', payload);
      const response = await httpService.post('/index.php/agenda/deletar_compromisso', payload, {
        responseContract: 'Agenda_DeletarCompromissoResponse'
      });
      log('useCompromisso', 'deleteCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'deleteCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel compromisso
   */
  const cancelarCompromisso = async (compromissoId: number) => {
    log('useCompromisso', 'cancelarCompromisso', 'Cancelling compromisso', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/compromissos/cancelar_compromisso/${compromissoId}`, {
        responseContract: 'Compromissos_CancelarCompromissoResponse',
      });
      log('useCompromisso', 'cancelarCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao cancelar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'cancelarCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Remove compromisso from agenda
   */
  const removerCompromisso = async (compromissoId: number) => {
    log('useCompromisso', 'removerCompromisso', 'Removing compromisso', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/compromissos/remover_compromisso/${compromissoId}`, {
        responseContract: 'Compromissos_RemoverCompromissoResponse',
      });
      log('useCompromisso', 'removerCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao remover compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'removerCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Finalize compromisso (complete)
   */
  const finalizarCompromisso = async (compromissoId: number) => {
    log('useCompromisso', 'finalizarCompromisso', 'Finalizing compromisso', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/compromissos/finaliza_compromisso_por_id/${compromissoId}`, {
        responseContract: 'Compromissos_FinalizaCompromissoPorIdResponse',
      });
      log('useCompromisso', 'finalizarCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao finalizar compromisso');
      }
    } catch (err) {
      log('useCompromisso', 'finalizarCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete service from compromisso
   */
  const deleteCompromissoServico = async (servicoId: any, orcamentoServicoId: any) => {
    log('useCompromisso', 'deleteCompromissoServico', 'Deleting service', { servicoId, orcamentoServicoId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/agenda/deleta_compromisso_servico/${servicoId}/${orcamentoServicoId}`, { responseContract: 'Agenda_DeletaCompromissoServicoResponse' });
      log('useCompromisso', 'deleteCompromissoServico', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar serviço');
      }
    } catch (err) {
      log('useCompromisso', 'deleteCompromissoServico', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available payment methods (formas de pagamento)
   */
  /** Com franquiaId, cada forma vem como vale naquela franquia (chamado 2026-0023). */
  const fetchFormasPagamento = async (franquiaId?: number | null) => {
    log('useCompromisso', 'fetchFormasPagamento', 'Fetching formas de pagamento', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const payload = (franquiaId ? { franquia_id: franquiaId } : {}) as Agenda_BuscarFormasPagamentoInput;
      const response = await httpService.post('/index.php/agenda/buscar_formas_pagamento', payload, {
        inputContract: 'Agenda_BuscarFormasPagamentoInput',
        responseContract: 'Agenda_BuscarFormasPagamentoResponse'
      });
      log('useCompromisso', 'fetchFormasPagamento', 'Response', response);

      if (response.success) {
        return response.dados?.formas_pagamento || [];
      } else {
        throw new Error('Erro ao buscar formas de pagamento');
      }
    } catch (err) {
      log('useCompromisso', 'fetchFormasPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Download receipt PDF for a completed compromisso
   *
   * The production system flow:
   * 1. First checks if PDF exists via /compromissos/buscaRecibo/{id}
   * 2. If exists, downloads from /uploads/documentos/{filename}
   * 3. If not exists, creates it via POST /compromissos/criarRecibo with HTML and owner_id
   * 4. Then downloads the newly created PDF
   *
   * @param {number} compromissoId - The compromisso ID
   * @param {Object} receiptData - Optional receipt data for HTML generation (compromisso, orcamento, translations)
   * @returns {string} PDF URL for download
   */
  const downloadReceipt = async (compromissoId: any, receiptData = null) => {
    log('useCompromisso', 'downloadReceipt', 'Downloading receipt PDF', { compromissoId, hasReceiptData: !!receiptData });
    isLoading.value = true;
    error.value = null;

    try {
      // First, check if receipt already exists
      const checkResponse = await httpService.get(`/index.php/compromissos/buscaRecibo/${compromissoId}`, {
        responseContract: 'Compromissos_BuscaReciboResponse',
      });

      log('useCompromisso', 'downloadReceipt', 'Check response', checkResponse);

      if (checkResponse.dados && checkResponse.dados.length > 0) {
        const pdfFilename = checkResponse.dados[0].Src;
        const pdfUrl = `/uploads/documentos/${pdfFilename}`;
        log('useCompromisso', 'downloadReceipt', 'Existing PDF found', pdfUrl);
        return pdfUrl;
      } else {
        // Receipt doesn't exist - need to create it
        if (!receiptData) {
          throw new Error('Dados do recibo não foram fornecidos para gerar o PDF.');
        }

        log('useCompromisso', 'downloadReceipt', 'PDF does not exist, creating new one');

        // Generate HTML using the receipt generator
        const { generateReciboHTML } = await import('../utils/reciboGenerator');
        const html = generateReciboHTML(receiptData);

        log('useCompromisso', 'downloadReceipt', 'Generated HTML length', html.length);

        // Create the PDF
        const payload = {
          HTML: html,
          owner_id: compromissoId
        };
        validate('Compromissos_CriarReciboInput', payload);
        const createResponse = await httpService.post('/index.php/compromissos/criarRecibo', payload, {
          responseContract: 'Compromissos_CriarReciboResponse',
          inputContract: 'Compromissos_CriarReciboInput'
        });

        log('useCompromisso', 'downloadReceipt', 'Create response', createResponse);

        if (createResponse.success) {
          // Recursively call to download the newly created PDF
          log('useCompromisso', 'downloadReceipt', 'PDF created, fetching download URL');
          return await downloadReceipt(compromissoId);
        } else {
          throw new Error(createResponse.error || 'Erro ao criar PDF do recibo');
        }
      }
    } catch (err) {
      log('useCompromisso', 'downloadReceipt', 'Error', { message: err.message, stack: err.stack });
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    fetchCompromissosList,
    fetchCompromisso,
    createCompromisso,
    updateCompromisso,
    deleteCompromisso,

    // Status operations
    cancelarCompromisso,
    removerCompromisso,
    finalizarCompromisso,

    // Service operations
    deleteCompromissoServico,

    // Helper data
    fetchFormasPagamento,

    // PDF operations
    downloadReceipt
  };
}
